import { useState, useEffect, useRef } from "react";
import ChatBotLogo from "../../../assets/images/svg/chat-bot-logo.svg";
import {
  CiCircleMinus,
  BsArrowDown,
  RiMessage2Line,
  BiSend,
  FaMicrophone,
  BiStop,
  BiPaperclip,
  BsVolumeMuteFill,
  BsVolumeUpFill,
} from "../../../assets/icons/vander";
import { sleep } from "../../../helpers";
import {
  useQuizStore,
  useFormEventStore,
  useSpeechRecognition,
  useSpeechSynthesis,
  usePatientSurgeryStore,
} from "../../hooks";
import { ErrorMessage, LoadingData } from "../../ui";
import { BotMessage, UserMessage, TypingIndicator, QuickActions } from "./";

export const VirtualAssistantChat = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    messages,
    uploadingFiles,

    // Methods
    initializeChatBot,
    sendUserMessage,
    sendQuizAnswers,
    setQuizMode,
    uploadFileForChatbot,
  } = useQuizStore();

  const {
    // Props
    patientSurgery,
  } = usePatientSurgeryStore();

  const {
    // Props
    isLoading,
    message,
  } = useFormEventStore();

  const {
    // Props
    isSpeaking,

    // Methods
    speakText,
    stopSpeaking,
  } = useSpeechSynthesis();

  const {
    isRecording,
    isListening,
    transcript,
    confidence,
    startRecording,
    stopRecording,
    isSupported: hasVoiceSupport,
  } = useSpeechRecognition(
    (finalTranscript, confidenceLevel) => {
      if (finalTranscript.trim()) {
        sendUserMessage(finalTranscript);
        setInputValue("");
        setVoiceError("");
      }
    },
    (errorMessage) => {
      console.error("Speech recognition error:", errorMessage);
      setVoiceError(errorMessage);
    }
  );

  const { assessmentQuestionGroups } =
    patientSurgery?.data?.procedure?.assessment || {};

  // States ------------------------------------------------//
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [shouldRenderButton, setShouldRenderButton] = useState(true);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [voiceError, setVoiceError] = useState("");
  const [allowFileUpload, setAllowFileUpload] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Formulario --------------------------------------------//
  // Funciones ---------------------------------------------//
  const scrollToBottom = async () => {
    await sleep(100);
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendUserMessage(inputValue);
      setInputValue("");
    }
  };

  const getCurrentQuestionId = () => {
    if (messages.length === 0) return null;
    const lastMessage = messages[messages.length - 1];
    return lastMessage.context?.current_question_id || null;
  };

  const handleToggle = async () => {
    setShouldRenderButton(false);

    if (!isOpen) {
      setQuizMode("chatbot");
      await sleep(50);
      setIsOpen(true);
      setShouldRender(true);
      setShouldRenderButton(true);
      initializeChatBot();
      return;
    }

    setQuizMode("traditional");
    if (inputValue.trim()) {
      setInputValue("");
    }

    stopSpeaking();
    await sleep(50);
    setIsOpen(false);
    setShouldRender(false);
    setShouldRenderButton(true);
    return;
  };

  const handleSpeakerToggle = () => {
    if (isSpeakerEnabled) {
      // Desactivar: detener audio y síntesis
      stopSpeaking();
      setIsSpeakerEnabled(false);
      return;
    }

    // Activar: reproducir último mensaje del bot
    setIsSpeakerEnabled(true);

    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === "bot" && lastMessage.content) {
        speakText(lastMessage, inputFocus);
      }
    }
  };

  const handleResize = () => {
    const visualViewport = window.visualViewport;
    if (visualViewport) {
      const keyboardHeight = window.innerHeight - visualViewport.height;
      setIsKeyboardVisible(keyboardHeight > 100);
    }
  };

  const checkMobile = () => {
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent.toLowerCase()
    );
  };

  const requestMicrophonePermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (error) {
      const errorMsg = isMobile
        ? "Permiso de micrófono denegado. Ve a Configuración > Sitios web > Permisos y habilita el micrófono para este sitio."
        : "Permiso de micrófono denegado. Habilítalo en configuración del navegador.";
      setVoiceError(errorMsg);
      return false;
    }
  };

  const inputFocus = async () => {
    await sleep(100);
    inputRef.current?.focus();
  };

  const sendQuizAnswersCallback = async () => {
    await sleep(100);
    sendQuizAnswers();
  };

  // Efectos -----------------------------------------------//
  // Manejo de apertura/cierre del chat y viewport
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      if (isMobile) document.body.style.overflow = "hidden";

      // Agregar listener para viewport solo cuando está abierto
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", handleResize);
      }
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();

      const lastMessage = messages[messages.length - 1];

      if (lastMessage.type === "bot" && lastMessage.content) {
        const audioCallback = lastMessage.shouldSubmit
          ? sendQuizAnswersCallback
          : inputFocus;

        if (isSpeakerEnabled) {
          speakText(lastMessage, audioCallback);
        } else {
          audioCallback();
        }

        let requiresFileUpload = false;

        if (assessmentQuestionGroups) {
          for (const group of assessmentQuestionGroups) {
            const question = group.questionGroup.questions.find(
              (q) =>
                q.followUpQuestion?.id ===
                lastMessage.context?.current_question_id
            );
            if (question?.followUpQuestion?.allowsFileUpload) {
              requiresFileUpload = true;
              break;
            }
          }
        }
        setAllowFileUpload(requiresFileUpload);
      }
    }
  }, [messages, assessmentQuestionGroups, isSpeakerEnabled]);

  useEffect(() => {
    if (voiceError) {
      const timer = setTimeout(() => setVoiceError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [voiceError]);

  // Scroll automático para diferentes eventos
  useEffect(() => {
    scrollToBottom();
  }, [isKeyboardVisible, isLoading]);

  // Detección de móvil y configuración inicial
  useEffect(() => {
    setIsMobile(checkMobile());
  }, []);

  // Mostrar instrucciones específicas para móvil
  useEffect(() => {
    if (isMobile && isOpen && messages.length === 0 && !hasVoiceSupport) {
      const timer = setTimeout(() => {
        setVoiceError(
          "El reconocimiento de voz no está disponible en este navegador. Intenta usar Chrome o Safari."
        );
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, isOpen, messages.length, hasVoiceSupport]);

  return (
    <>
      {/* Botón flotante */}
      {shouldRenderButton && !isKeyboardVisible && (
        <button
          className="animate__animated animate__bounceInUp animate__faster 
          position-fixed btn btn-primary btn-icon btn-lg rounded-circle z-3"
          style={{ right: "20px", bottom: "20px" }}
          onClick={handleToggle}
        >
          {isOpen ? (
            <BsArrowDown className="icon" />
          ) : (
            <RiMessage2Line className="icon" />
          )}
        </button>
      )}

      {/* Chat */}
      {shouldRender && (
        <div
          className="virtual-assistant-chat position-fixed z-2"
          style={{
            right: "20px",
            bottom: !isKeyboardVisible ? "70px" : "20px",
          }}
        >
          {/* Header */}
          <div className="header">
            <img src={ChatBotLogo} style={{ width: "48px", height: "48px" }} />
            <div className="d-inline-flex flex-column">
              <h4 className="mb-0">Habla con bot</h4>
              <span style={{ fontSize: "12px", color: "#43EE7D" }}>
                ● En linea
              </span>
            </div>
            <button
              className="btn p-0"
              onClick={handleSpeakerToggle}
              title={isSpeakerEnabled ? "Desactivar audio" : "Activar audio"}
            >
              {isSpeakerEnabled ? (
                <BsVolumeUpFill style={{ width: "32px", height: "32px" }} />
              ) : (
                <BsVolumeMuteFill style={{ width: "32px", height: "32px" }} />
              )}
            </button>
            <button
              title="Cerrar"
              className="btn p-0 ms-auto"
              onClick={() => handleToggle()}
            >
              <CiCircleMinus style={{ width: "24px", height: "24px" }} />
            </button>
          </div>

          {/* Body - Messages */}
          <div className="messages-area">
            {messages.map((message, index) =>
              message.type === "user" ? (
                <UserMessage
                  key={index}
                  message={message.content}
                  timestamp={message.timestamp}
                />
              ) : (
                <BotMessage
                  key={index}
                  message={message.content}
                  timestamp={message.timestamp}
                />
              )
            )}

            {isLoading && <TypingIndicator />}

            <div ref={messagesEndRef} />

            {message?.type === "error" && (
              <ErrorMessage>{message.text}</ErrorMessage>
            )}
          </div>

          {/* Footer - Input */}
          <div className="footer">
            <QuickActions
              actions={[
                {
                  text: "🤔 Quiero llenar la encuesta por mensaje de voz",
                  onClick: () =>
                    sendUserMessage(
                      "Quiero llenar la encuesta por mensaje de voz"
                    ),
                },
                {
                  text: "📝 Necesito ayuda con el formulario",
                  onClick: () =>
                    sendUserMessage("Necesito ayuda con el formulario"),
                },
                {
                  text: isMobile
                    ? "🎤 Grabar con voz"
                    : "🔊 Prefiero usar audio",
                  onClick: async () => {
                    if (isMobile) {
                      const hasPermission = await requestMicrophonePermission();
                      if (!hasPermission) return;
                    }
                    startRecording();
                  },
                },
              ]}
            />

            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              accept="image/*,.pdf"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const questionId = getCurrentQuestionId();
                  uploadFileForChatbot(questionId || "chatbot-file", file);
                }
              }}
            />

            <div className="chat-input">
              <input
                ref={inputRef}
                type="text"
                placeholder={isRecording ? "Escuchando..." : "Mensaje"}
                value={isRecording ? transcript : inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyUp={handleKeyPress}
                disabled={isLoading || isRecording || isSpeaking}
              />
              <div className="input-buttons">
                {allowFileUpload && (
                  <button
                    className="input-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading || isRecording || isSpeaking}
                    title="Subir archivo"
                  >
                    <BiPaperclip />
                  </button>
                )}

                <button
                  className="input-btn"
                  onClick={() => {
                    sendUserMessage(inputValue);
                    setInputValue("");
                  }}
                  disabled={
                    isLoading ||
                    (!inputValue.trim() && !transcript.trim()) ||
                    isRecording ||
                    isSpeaking
                  }
                >
                  <BiSend className="send-btn" />
                </button>

                {hasVoiceSupport && (
                  <button
                    className={`input-btn ${
                      isRecording
                        ? isListening
                          ? "listening-pulse"
                          : "recording"
                        : ""
                    }`}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isLoading || isSpeaking}
                    title={
                      isRecording
                        ? "Detener grabación"
                        : "Grabar respuesta con voz"
                    }
                  >
                    {isRecording ? (
                      <BiStop className="mic-btn" />
                    ) : (
                      <FaMicrophone className="mic-btn" />
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Instrucciones para móvil */}
            {isMobile && !hasVoiceSupport && (
              <div className="alert alert-info alert-sm mt-2 p-2">
                <small>
                  📱 <strong>Consejo:</strong> Para usar el reconocimiento de
                  voz, abre este sitio en Chrome o Safari.
                </small>
              </div>
            )}

            {/* Estados de carga y errores */}
            {uploadingFiles[getCurrentQuestionId()]?.isLoading && (
              <LoadingData />
            )}

            {uploadingFiles[getCurrentQuestionId()]?.message?.type ===
              "error" && (
              <ErrorMessage>
                {
                  uploadingFiles[getCurrentQuestionId() || "chatbot-file"]
                    ?.message?.text
                }
              </ErrorMessage>
            )}

            {isRecording && (
              <div className="recording-status">
                <small
                  className={isListening ? "text-success" : "text-warning"}
                >
                  <span
                    className={`recording-dot ${
                      isListening ? "text-success" : "text-warning"
                    }`}
                  >
                    ●
                  </span>
                  {isListening ? "Escuchando... Habla ahora" : "Procesando..."}
                </small>
                {confidence > 0 && (
                  <>
                    <small className="d-block text-muted mt-1">
                      Confianza: {Math.round(confidence * 100)}%
                    </small>
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${confidence * 100}%` }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            )}

            {voiceError && <small className="text-danger">{voiceError}</small>}
          </div>
        </div>
      )}
    </>
  );
};
